import React, { useContext, useState } from "react";
import { Switch, Field, Description, Transition } from '@headlessui/react'
import { FormContext } from "./CreateInvoiceFundForm";
import { useStore } from 'zustand'
import ScheduleSelector from './ScheduleSelector';
import { CalendarDaysIcon, ClockIcon, CheckCircleIcon } from '@heroicons/react/20/solid'

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

const ToggleAutoInvoicing = ({ autoInvoice, setAutoInvoice }) => {
  return (
    <div className={classNames(
      autoInvoice ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200',
      'rounded-xl border-2 p-4 transition-all duration-200'
    )}>
      <input type="hidden" name="fund[auto_invoice]" value={autoInvoice} />
      <div className="flex items-start gap-4">
        <div className={classNames(
          autoInvoice ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-500',
          'w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors'
        )}>
          <ClockIcon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <div>
              <h4 className={classNames(
                autoInvoice ? 'text-blue-900' : 'text-gray-900',
                'text-sm font-semibold'
              )}>
                Automatic Invoice Generation
              </h4>
              <p className={classNames(
                autoInvoice ? 'text-blue-700' : 'text-gray-500',
                'text-sm mt-0.5'
              )}>
                Invoices are automatically created before each due date
              </p>
            </div>
            <Switch
              checked={autoInvoice}
              onChange={setAutoInvoice}
              className={classNames(
                autoInvoice ? 'bg-blue-600' : 'bg-gray-300',
                'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2'
              )}
            >
              <span
                aria-hidden="true"
                className={classNames(
                  autoInvoice ? 'translate-x-5' : 'translate-x-0',
                  'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                )}
              />
            </Switch>
          </div>

          {autoInvoice && (
            <div className="mt-3 flex items-center gap-4 text-xs text-blue-600">
              <div className="flex items-center gap-1">
                <CheckCircleIcon className="w-4 h-4" />
                <span>Auto-generates invoices</span>
              </div>
              <div className="flex items-center gap-1">
                <CheckCircleIcon className="w-4 h-4" />
                <span>Works with reminders</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const InvoiceScheduleForm = (props) => {
  const store = useContext(FormContext);
  const { invoicingFund, setAutoInvoice } = useStore(store);
  const autoInvoice = invoicingFund.auto_invoice;
  const initialFundRruleIcal = invoicingFund.ical_rrule;

  return (
    <div className="space-y-4">
      {/* Section Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0">
          <CalendarDaysIcon className="w-5 h-5" />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Invoice Scheduling</h3>
          <p className="text-xs text-gray-500">Configure automatic invoice generation and billing cycle</p>
        </div>
      </div>

      {/* Toggle Card */}
      <ToggleAutoInvoicing autoInvoice={autoInvoice} setAutoInvoice={setAutoInvoice} />

      {/* Schedule Configuration (shown when auto invoice is enabled) */}
      {autoInvoice && (
        <div className="mt-4 p-5 bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl border border-gray-200">
          <div className="flex items-center gap-2 mb-4">
            <CalendarDaysIcon className="w-5 h-5 text-gray-600" />
            <h4 className="text-sm font-semibold text-gray-900">Billing Schedule</h4>
          </div>

          <div className="mb-4 p-3 bg-blue-50 border border-blue-100 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>How it works:</strong> Invoices are automatically generated before each due date based on your schedule below. Members will receive their invoice ahead of time so they can pay by the due date.
            </p>
          </div>

          <ScheduleSelector
            label="Invoice Due Date Schedule"
            fieldName="fund[ical_rrule]"
            initialFundRruleIcal={initialFundRruleIcal}
          />
        </div>
      )}
    </div>
  )
}

export default InvoiceScheduleForm;